import React, { useState, useEffect, useRef } from "react";
import "./studentnotes.css";
import dayjs from "dayjs";
import ErrorImg from "../../Assets/Images/CommanImages/Error.png";
import { GET } from "../../Api/HttpsMethods";
import { POST } from "../../Api/HttpsMethods";
import EndPoints from "../../Api/EndPoints";

const StudentNotesSection = ({
  selectedStudentID,
  clientBranchId,
  userName,
  setMessageBox,
  setIsMessageBoxVisible,
  setActiveImg,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // ── Auto-scroll to bottom whenever items change ──
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [items]);

  // ── Auto-grow textarea height ──
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  // ── Fetch notes whenever student or branch changes ──
  useEffect(() => {
    if (selectedStudentID && clientBranchId) {
      fetchStudentNotes();
    } else {
      setItems([]);
    }
  }, [selectedStudentID, clientBranchId]);

  // ── Reset panel state when student changes ──
  useEffect(() => {
    setItems([]);
    setInputValue("");
    setIsChecked(false);
  }, [selectedStudentID]);

  // ── Fetch notes from API ──
  const fetchStudentNotes = async () => {
    if (!selectedStudentID || !clientBranchId) {
      setItems([]);
      return;
    }
    setIsLoading(true);
    const url = `${EndPoints.GetStudentNotes}?student_id=${selectedStudentID}&branch_id=${clientBranchId}`;
    const data = await GET.withToken(url, {
      setMessageBox,
      setIsMessageBoxVisible,
      setActiveImg,
      ErrorImg,
    });
    setIsLoading(false);

    if (data && data.status === 200 && Array.isArray(data.data)) {
      const formattedNotes = data.data.map((note) => ({
        date: dayjs(note.create_date).format("YYYY-MM-DD HH:mm"),
        text: note.note_text,
        isChecked: note.disciplinary_flag,
        isRed: note.disciplinary_flag,
        username: note.username,
      }));
      setItems(formattedNotes);
    } else {
      setItems([]);
      console.error("Unexpected response format:", data);
    }
  };

  // ── Post a new note to API ──
  const postStudentNote = async (noteText, disciplinaryFlag) => {
    const url = `${
      EndPoints.AddStudentNote
    }?student_id=${selectedStudentID}&note_text=${encodeURIComponent(
      noteText
    )}&branch_id=${clientBranchId}&username=${userName}&device=web&disciplinary_flag=${disciplinaryFlag}`;

    const data = await POST.withToken(
      url,
      {},
      {
        setMessageBox,
        setIsMessageBoxVisible,
        setActiveImg,
        ErrorImg,
      }
    );
    return data;
  };

  // ── Handle Enter key to submit note ──
  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!inputValue.trim()) return;

      // Support @KeepRed prefix to force red color
      const isRed = inputValue.startsWith("@KeepRed");
      const cleanText = isRed
        ? inputValue.replace("@KeepRed", "").trim()
        : inputValue;

      const newItem = {
        date: dayjs().format("YYYY-MM-DD HH:mm"),
        username: userName,
        text: cleanText,
        isChecked: isChecked,
        isRed: isRed || isChecked,
      };

      // Optimistic UI update — show instantly, post in background
      setItems((prev) => [...prev, newItem]);
      setInputValue("");
      setIsChecked(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      // Post to server
      await postStudentNote(cleanText, isChecked);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleCheckboxChange = () => {
    setIsChecked((prev) => !prev);
  };

  return (
    <div className={`std-dash-panel${isOpen ? " std-dash-panel--open" : ""}`}>
      {/* ── Slide-out tab ── */}
      <div
        className="std-dash-toggle-btn"
        onClick={() => setIsOpen((prev) => !prev)}
        title={isOpen ? "Close Student Notes" : "Open Student Notes"}
      >
        STUDENT NOTES
      </div>

      {/* ── Panel content ── */}
      <div className="std-dash-content">
        {/* Notes list */}
        <div className="std-dash-history" ref={scrollRef}>
          {isLoading ? (
            <div className="std-dash-empty">Loading notes…</div>
          ) : items.length === 0 ? (
            <div className="std-dash-empty">No notes found for this student.</div>
          ) : (
            <ul className="std-dash-notes-list">
              {items.map((item, index) => (
                <li key={index} className="std-dash-note-item">
                  <span className="std-dash-note-date">{item.date}</span>
                  <div className="std-dash-note-body">
                    <span className="std-dash-note-user">[{item.username}]</span>
                    <span
                      className="std-dash-note-text"
                      style={{ color: item.isRed ? "#FA4741" : undefined }}
                    >
                      {item.text}
                    </span>
                    {item.isRed && (
                      <span className="std-dash-disciplinary-badge">
                        Disciplinary
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Input area ── */}
        <div className="std-dash-input-area">
          <div className="std-dash-input-row">
            <span className="std-dash-input-dot" aria-hidden="true" />
            <span className="std-dash-input-meta">
              {dayjs().format("YYYY-MM-DD HH:mm")}
            </span>
            <span className="std-dash-input-user">[{userName}]</span>
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="std-dash-textarea"
              rows={1}
              placeholder="Type your note and press Enter"
              style={{
                color:
                  isChecked || inputValue.startsWith("@KeepRed")
                    ? "#FA4741"
                    : undefined,
              }}
            />
          </div>

          <div className="std-dash-flag-row">
            <label className="std-dash-flag-label" htmlFor="std-dash-disc-flag">
              Disciplinary Flag
            </label>
            <input
              id="std-dash-disc-flag"
              // className="std-dash-flag-checkbox"
              className="stu-db-disciplinary-flag-checkbox"
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentNotesSection;